import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowRight, Shield, Truck, Sparkles } from "lucide-react";
import { fetchProducts, formatUsd } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useUtmCapture } from "../lib/utm";

// Slug → product_id normaliser. Marketing sends kebab-case slugs;
// catalog uses underscores. Also honours common short slugs.
const SLUG_MAP = {
  "liposuction": "liposuction", "lipo": "liposuction",
  "breast-augmentation": "breast_augmentation", "breast": "breast_augmentation", "boob-job": "breast_augmentation",
  "tummy-tuck": "tummy_tuck", "abdominoplasty": "tummy_tuck",
  "mommy-makeover": "mommy_makeover", "mommy": "mommy_makeover",
  "facelift": "facelift", "face-lift": "facelift",
  "injectables-lasers": "injectables_lasers", "injectables": "injectables_lasers",
  "lasers": "injectables_lasers", "botox": "injectables_lasers",
};

const CLAIMS = {
  liposuction: {
    hero_line: "Everything your body needs after lipo, curated in one box.",
    subline: "Marena compression, Silagen scar care, LARQ hydration — sequenced for the first 6 weeks after body contouring.",
    proof: [
      { icon: Shield, title: "Surgeon-selected", body: "Every item is a real premium brand board-certified plastic surgeons already prescribe." },
      { icon: Truck, title: "Arrives before day one", body: "Give us your surgery date. Your kit lands 3–4 days ahead — no rushing on the way home." },
      { icon: Sparkles, title: "Feels considered", body: "Warm, spa-like packaging. Nothing about your recovery should feel clinical." },
    ],
    testimonial: {
      quote: "I opened it the night before surgery and felt cared for. Everything I needed for six weeks was there.",
      author: "Isabel R., Beverly Hills",
    },
  },
  breast_augmentation: {
    hero_line: "Recover from breast surgery like your surgeon planned it.",
    subline: "Marena Caress + Amoena Pamela + Alastin — the bras and serums your surgeon prescribes, delivered in one system.",
    proof: [
      { icon: Shield, title: "The right two bras", body: "Stage-1 pocketed bra for weeks 1–2. Seamless Amoena for weeks 3+." },
      { icon: Truck, title: "Elevation from night one", body: "Sleep Again upper-body wedge included — because sleeping upright matters." },
      { icon: Sparkles, title: "Bruise care that works", body: "Alastin INhance ChromaFADE for visible redness reduction in days." },
    ],
    testimonial: {
      quote: "I didn't have to think about what to buy. Everything my PS listed off was in the box.",
      author: "Amelia W., Miami",
    },
  },
  tummy_tuck: {
    hero_line: "A full 8-week abdominoplasty protocol, in one system.",
    subline: "Two-stage Marena + Design Veronique compression, Kelo-Cote scar therapy, and complete drain management.",
    proof: [
      { icon: Shield, title: "Compression that transitions", body: "Stage-1 binder for weeks 1–2. Powernet stage-2 for the rest of your recovery." },
      { icon: Truck, title: "Drain-day essentials", body: "Waist-worn drain holder, shower lanyard, output journal — thought through." },
      { icon: Sparkles, title: "Scars that fade", body: "Kelo-Cote sheets plus Silagen SPF gel — the two-part protocol surgeons cite." },
    ],
    testimonial: {
      quote: "The drain kit alone was worth it. I felt human by week two.",
      author: "Priya S., Dallas",
    },
  },
  mommy_makeover: {
    hero_line: "For the recovery that's really two surgeries at once.",
    subline: "Marena full-body faja, Caress pocketed bra, SkinCeuticals scar therapy — the most comprehensive ALTA system.",
    proof: [
      { icon: Shield, title: "Two surgeries, one system", body: "Combined torso and breast compression sized to work together, not fight each other." },
      { icon: Truck, title: "Positioning built in", body: "Complete Sleep Again pillow system so you can rest on your back without slipping." },
      { icon: Sparkles, title: "Premium scar care", body: "SkinCeuticals 95% silicone gel + Kelo-Cote sheets on every incision line." },
    ],
    testimonial: {
      quote: "I had my PS list on my phone. Every item was in the ALTA box. I cried a little.",
      author: "Michelle T., Atlanta",
    },
  },
  facelift: {
    hero_line: "Bounce back from a facelift like the editors do.",
    subline: "Fabric compression, Charlotte Tilbury cryo mask, and Alastin's regenerating nectar — a 4-week facial recovery system.",
    proof: [
      { icon: Shield, title: "Compression that fits", body: "Adjustable Leonisa wrap with cold-pack pockets — not the flimsy strap you'd get free." },
      { icon: Truck, title: "Cryo therapy included", body: "Charlotte Tilbury silicone cryo mask, chill and apply — no bags of frozen peas." },
      { icon: Sparkles, title: "Barrier repair", body: "Alastin's TriHex+ nectar to keep your skin strong before and after." },
    ],
    testimonial: {
      quote: "I looked presentable at two weeks. The cryo mask was a game-changer.",
      author: "Kate D., New York",
    },
  },
  injectables_lasers: {
    hero_line: "The 2-week recovery kit dermatologists layer post-treatment.",
    subline: "Alastin INhance + TriHex+, iS Clinical Hydra-Cool, Augustinus Bader Wound Gel, mineral SPF — the professional post-treatment routine.",
    proof: [
      { icon: Shield, title: "For neuromod, filler & laser", body: "Whether it's Botox, filler, RF micro-needling or CO2 — the layering protocol works." },
      { icon: Truck, title: "Sun protection is mandatory", body: "Colorescience or ISDIN mineral SPF included — non-negotiable after laser." },
      { icon: Sparkles, title: "Bruises fade faster", body: "Alastin INhance ChromaFADE speeds bruise clearance meaningfully." },
    ],
    testimonial: {
      quote: "My derm literally handed me this list. Nice to have it curated.",
      author: "Vera K., Los Angeles",
    },
  },
};

function useProduct(slug) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchProducts()
      .then((list) => {
        if (!alive) return;
        const id = SLUG_MAP[slug] || SLUG_MAP[slug?.toLowerCase()];
        setProduct(list.find((p) => p.id === id) || null);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);
  return { product, loading };
}

export default function LandingPage() {
  const { procedure } = useParams();
  const nav = useNavigate();
  const { addItem } = useCart();
  const { product, loading } = useProduct(procedure);
  useUtmCapture();

  if (loading) {
    return <div className="min-h-[70vh] flex items-center justify-center font-serif text-2xl text-[#6B5757]">Loading…</div>;
  }
  if (!product) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-6 text-center">
        <div className="eyebrow">Not Found</div>
        <h1 className="font-serif text-4xl mt-4">We couldn't find that recovery system.</h1>
        <Link data-testid="landing-back-shop" to="/shop" className="btn-primary mt-8">
          Browse All Systems
        </Link>
      </div>
    );
  }

  const content = CLAIMS[product.id] || {
    hero_line: `Your ${product.procedure.toLowerCase()} recovery, curated.`,
    subline: product.subtitle,
    proof: [
      { icon: Shield, title: "Surgeon-selected", body: "Every item is a real premium brand." },
      { icon: Truck, title: "Arrives on time", body: "Delivery matched to your surgery date." },
      { icon: Sparkles, title: "Feels considered", body: "Spa-like packaging, sequenced for you." },
    ],
    testimonial: { quote: "The recovery you deserve.", author: "ALTA Community" },
  };
  const savings = Math.max(0, (product.bundle_msrp || 0) - product.price);

  const addAndCheckout = () => {
    addItem(product, 1);
    toast.success("Added to cart");
  };

  const seeDetails = () => nav(`/products/${product.id}`);

  return (
    <div className="bg-[#FBF6F1]">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 px-4 sm:px-8 lg:px-16 py-16 lg:py-24 items-center">
          <div>
            <div className="eyebrow" data-testid="landing-eyebrow">Recovery System · {product.procedure}</div>
            <h1
              data-testid="landing-hero-headline"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 text-[#2A1F1F]"
            >
              {content.hero_line}
            </h1>
            <p className="text-[#6B5757] mt-6 text-base sm:text-lg leading-relaxed max-w-xl">
              {content.subline}
            </p>

            <div className="mt-8 flex items-baseline gap-4">
              <span className="font-serif text-4xl text-[#2A1F1F]" data-testid="landing-price">{formatUsd(product.price)}</span>
              {savings > 0 && (
                <span className="text-sm text-[#6B5757] line-through">{formatUsd(product.bundle_msrp)}</span>
              )}
              {savings > 0 && (
                <span className="text-xs font-mono uppercase tracking-widest text-[#8B9E7E]">
                  Save {formatUsd(savings)}
                </span>
              )}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="landing-cta-primary"
                onClick={addAndCheckout}
                className="btn-primary inline-flex items-center gap-3"
              >
                Add to Cart <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid="landing-cta-secondary"
                onClick={seeDetails}
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#2A1F1F] text-[#2A1F1F] text-sm rounded-full hover:bg-[#2A1F1F] hover:text-[#FBF6F1] transition-colors"
              >
                See what's inside
              </button>
            </div>

            <ul className="mt-10 space-y-2 text-sm text-[#4A3535]">
              {product.included.slice(0, 4).map((it, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <Check className="w-4 h-4 text-[#8B9E7E] mt-0.5" strokeWidth={2} />
                  <span><span className="font-medium">{it.brand}</span> · {it.name}</span>
                </li>
              ))}
              {product.included.length > 4 && (
                <li className="text-xs text-[#6B5757] pl-7">
                  + {product.included.length - 4} more premium items
                </li>
              )}
            </ul>
          </div>

          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
            <img
              src={product.image}
              alt={product.name}
              data-testid="landing-hero-image"
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute bottom-6 left-6 right-6 flex items-center gap-3 bg-[#FBF6F1]/90 backdrop-blur px-4 py-3 rounded-lg">
              <div className="flex text-[#B89B66] text-sm">
                {"★★★★★".slice(0, Math.round(product.rating))}
              </div>
              <span className="text-xs font-mono uppercase tracking-widest text-[#6B5757]">
                {product.rating.toFixed(1)} · {product.reviews} verified
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* PROOF POINTS */}
      <section className="border-y border-[#F0DFD0] bg-white">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-10 px-4 sm:px-8 lg:px-16 py-16">
          {content.proof.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} data-testid={`landing-proof-${i}`} className="flex flex-col">
                <div className="w-12 h-12 rounded-full bg-[#FDF3ED] flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#B85C56]" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-xl text-[#2A1F1F]">{p.title}</h3>
                <p className="text-sm text-[#6B5757] mt-3 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-16 py-20 text-center">
        <div className="eyebrow">In their words</div>
        <blockquote
          data-testid="landing-testimonial"
          className="font-serif text-2xl sm:text-3xl leading-snug text-[#2A1F1F] mt-6"
        >
          "{content.testimonial.quote}"
        </blockquote>
        <div className="text-xs font-mono uppercase tracking-widest text-[#6B5757] mt-6">
          — {content.testimonial.author}
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-[#2A1F1F] text-[#FBF6F1]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-16 py-16 text-center">
          <div className="eyebrow" style={{ color: "#E5C1AF" }}>Ready when you are</div>
          <h2 className="font-serif text-3xl sm:text-4xl mt-4">Give yourself the recovery you deserve.</h2>
          <p className="text-[#E5C1AF]/80 mt-4 max-w-lg mx-auto text-sm">
            Free shipping in the US. Delivery timed to your surgery date. 30-day return on unopened items.
          </p>
          <button
            data-testid="landing-cta-band"
            onClick={addAndCheckout}
            className="mt-8 inline-flex items-center gap-3 bg-[#FBF6F1] text-[#2A1F1F] px-8 py-4 rounded-full text-sm font-medium tracking-wide hover:bg-white transition-colors"
          >
            Add {product.name} to Cart · {formatUsd(product.price)} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>
    </div>
  );
}
